import React, { useState, useEffect, useRef } from 'react';
import './Gallery.css';

interface GalleryItem {
  id: string;
  src: string;
  title: string;
  description: string;
  category: 'press' | 'artwork' | 'events' | 'community';
}

const Gallery: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<GalleryItem | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const sectionRef = useRef<HTMLElement>(null);

  const galleryItems: GalleryItem[] = [
    {
      id: '1',
      src: '/images/newsclippings/IMG_1203.png',
      title: 'Sound Life Feature',
      description: 'Featured in Sound Life magazine highlighting downtown revitalization and gallery success',
      category: 'press'
    },
    {
      id: '2',
      src: '/images/newsclippings/IMG_1204.png',
      title: 'Art Galleries Coverage',
      description: 'Local news coverage discussing the evolution of Tacoma\'s art scene',
      category: 'press'
    },
    {
      id: '3',
      src: '/images/newsclippings/IMG_1206.png',
      title: 'Studio Environment',
      description: 'Hector in his creative space, surrounded by his artistic works',
      category: 'artwork'
    },
    {
      id: '4',
      src: '/images/newsclippings/IMG_1207.png',
      title: 'Finding Light in the Darkness',
      description: 'The News Tribune feature on Hector\'s nocturnal painting style',
      category: 'press'
    },
    {
      id: '5',
      src: '/images/newsclippings/IMG_1210.png',
      title: 'Artistic Vision',
      description: 'Showcase of Hector\'s distinctive painting style and technique',
      category: 'artwork'
    },
    {
      id: '6',
      src: '/images/newsclippings/IMG_1215.png',
      title: 'Community Recognition',
      description: 'Multiple media features highlighting Hector\'s community impact',
      category: 'community'
    },
    {
      id: '7',
      src: '/images/newsclippings/IMG_1220.png',
      title: 'City Paper Feature',
      description: 'Tacoma City Paper coverage welcoming new artistic developments',
      category: 'press'
    }
  ];

  const categories = [
    { key: 'all', label: 'All Items' },
    { key: 'press', label: 'Press Coverage' },
    { key: 'artwork', label: 'Artistic Works' },
    { key: 'community', label: 'Community Impact' },
    { key: 'events', label: 'Events & Awards' }
  ];

  const filteredItems = filter === 'all'
    ? galleryItems
    : galleryItems.filter(item => item.category === filter);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.hector-gallery-item');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [filteredItems]);

  const openModal = (item: GalleryItem) => {
    setSelectedImage(item);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'press': return '📰';
      case 'artwork': return '🎨';
      case 'events': return '🏆';
      case 'community': return '🤝';
      default: return '✨';
    }
  };

  return (
    <section className="gallery-section hector-gallery" id="gallery" ref={sectionRef}>
      <div className="gallery-container">
        <div className="gallery-header hector-fade-in-up">
          <h2 className="hector-section-title">Visual Legacy</h2>
          <p className="gallery-subtitle">
            Explore the documented journey of Hector Díaz through press coverage,
            artistic works, and community recognition
          </p>
          <div className="hector-horizon-line"></div>
        </div>

        <div className="gallery-filters hector-fade-in-up">
          {categories.map((category) => (
            <button
              key={category.key}
              className={`filter-btn ${filter === category.key ? 'active' : ''}`}
              onClick={() => setFilter(category.key)}
            >
              {getCategoryIcon(category.key)} {category.label}
            </button>
          ))}
        </div>

        <div className="gallery-grid">
          {filteredItems.map((item, index) => (
            <div
              key={item.id}
              className="hector-gallery-item"
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => openModal(item)}
            >
              <div className="gallery-card">
                <div className="gallery-image-container">
                  <img
                    src={item.src}
                    alt={item.title}
                    loading="lazy"
                  />
                  <div className="gallery-overlay">
                    <div className="gallery-content">
                      <h3 className="gallery-title">{item.title}</h3>
                      <p className="gallery-description">{item.description}</p>
                      <div className="gallery-category">
                        <span className={`category-badge ${item.category}`}>
                          {getCategoryIcon(item.category)} {item.category}
                        </span>
                      </div>
                    </div>
                    <div className="learn-more-section">
                      <div className="learn-more-content">
                        <div className="learn-more-header">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L13.5 7.5L19 9L13.5 10.5L12 15L10.5 10.5L5 9L10.5 7.5L12 2Z" fill="currentColor"/>
                          </svg>
                          <span className="learn-more-title">Explore Details</span>
                        </div>
                        <div className="learn-more-details">
                          {item.category === 'press' && (
                            <p>Featured media coverage documenting Hector's artistic impact and community contributions</p>
                          )}
                          {item.category === 'artwork' && (
                            <p>Original artistic works showcasing Hector's unique nocturnal painting style and vision</p>
                          )}
                          {item.category === 'community' && (
                            <p>Recognition of Hector's transformative impact on local arts education and community development</p>
                          )}
                          {item.category === 'events' && (
                            <p>Special events and awards celebrating Hector's artistic achievements and legacy</p>
                          )}
                        </div>
                      </div>
                      <div className="action-buttons">
                        <button className="learn-more-btn" onClick={(e) => { e.stopPropagation(); openModal(item); }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 7V9L21 9ZM3 9V7L9 7V9L3 9Z" fill="currentColor"/>
                          </svg>
                          Learn More
                        </button>
                        <button className="view-full-btn" onClick={(e) => { e.stopPropagation(); openModal(item); }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 5V7H7V9H5V7C5 5.9 5.9 5 7 5H9ZM11 5H13V7H11V5ZM15 5H17C18.1 5 19 5.9 19 7V9H17V7H15V5ZM5 11H7V13H5V11ZM17 11H19V13H17V11ZM5 15H7V17H9V19H7C5.9 19 5 18.1 5 17V15ZM11 19H13V17H11V19ZM15 19V17H17V15H19V17C19 18.1 18.1 19 17 19H15Z" fill="currentColor"/>
                          </svg>
                          View Full Size
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredItems.length === 0 && (
          <div className="no-items">
            <p>No items found for the selected category.</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedImage && (
        <div className="gallery-modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            <div className="modal-image-container">
              <img src={selectedImage.src} alt={selectedImage.title} />
            </div>
            <div className="modal-info">
              <h3>{selectedImage.title}</h3>
              <p>{selectedImage.description}</p>
              <span className={`category-badge ${selectedImage.category}`}>
                {getCategoryIcon(selectedImage.category)} {selectedImage.category}
              </span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;